<i18n
  src="@/locales/views/organization/process/harvest_export/show_process.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/exported_batch.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/harvested_batch.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/producer.yml" lang="yaml"></i18n>

<template>
  <go-back-link :text="$t('labels.go_back_link')" :name="routeName('list_processes')"
    :params="{ organization_id: organization_id }" />

  <bread-crumbs :labels="[module_name, harvest_process?.name]" />

  <template v-if="load_data_general">
    <h1 class="page-title">
      <i class="fa-solid fa-crate-apple"></i>
      <span class="space-icon"></span>
      {{ $t("labels.title") }}
    </h1>

    <div class="mt-6 flex flex-row justify-between">
      <div class="panel-plain-label">
        <badge-default :label="$t('labels.total_exported_batches', {
          batches_number: total_exported_batches,
        })
          " />
      </div>
      <div class="space-x-3">
        <button v-if="exported_batches.length > 0" type="button" @click.prevent="downloadRepHarvestXlsx()"
          class="btn-white hidden md:inline">
          <i :class="[
            'fa-solid',
            rep_harvest_downloading ? 'fa-sync fa-spin' : 'fa-file-excel',
          ]">
          </i>
          <span class="space-icon"></span>
          {{ $t("labels.download_harvest_button") }}
        </button>
        <button v-if="exported_batches.length > 0" type="button" @click.prevent="downloadRepPackingXlsx()"
          class="btn-white hidden md:inline">
          <i :class="[
            'fa-solid',
            rep_packing_downloading ? 'fa-sync fa-spin' : 'fa-file-excel',
          ]">
          </i>
          <span class="space-icon"></span>
          {{ $t("labels.download_packing_button") }}
        </button>
        <button v-if="exported_batches.length > 0" type="button" @click.prevent="downloadRepConsolidatedXlsx()"
          class="btn-white hidden md:inline">
          <i :class="[
            'fa-solid',
            rep_consolidated_downloading
              ? 'fa-sync fa-spin'
              : 'fa-file-excel',
          ]">
          </i>
          <span class="space-icon"></span>
          {{ $t("labels.download_consolidated_button") }}
        </button>
        <new-button @click.prevent="goToNewExportedBatch()" :label="$t('labels.new_button')" />
      </div>
    </div>
    <div class="mt-4 flex flex-row-reverse gap-2">
      <white-button v-if="filtered" label="Limpiar" @click="clearFilters" />
      <white-button label="Filtrar" @click="filterExportedBatches" />
      <input type="text" v-model.trim="filters.crop_field_name" :placeholder="$t('model.exported_batch.crop_field')"
        class="shadow-sm focus:ring-lime-500 focus:border-lime-500 block sm:text-sm border-gray-300 rounded-md" />
      <input type="text" v-model.trim="filters.producer_fullname" :placeholder="$t('model.exported_batch.producer')"
        class="shadow-sm focus:ring-lime-500 focus:border-lime-500 block sm:text-sm border-gray-300 rounded-md" />
      <input type="text" v-model.trim="filters.organization_producer_code"
        :placeholder="$t('model.producer.organization_producer.producer_code') + ' ' + $t('model.exported_batch.producer')"
        class="shadow-sm focus:ring-lime-500 focus:border-lime-500 block sm:text-sm border-gray-300 rounded-md" />
      <input type="text" v-model.trim="filters.producer_code" :placeholder="$t('model.producer.producer_code')"
        class="shadow-sm focus:ring-lime-500 focus:border-lime-500 block sm:text-sm border-gray-300 rounded-md" />
      <input type="text" v-model.trim="filters.batch_code" :placeholder="$t('model.exported_batch.batch_code')"
        class="shadow-sm focus:ring-lime-500 focus:border-lime-500 block sm:text-sm border-gray-300 rounded-md" />
    </div>
    <div class="mt-4 flex flex-col">
      <ag-grid-vue style="width: 100%; height: 650px" class="ag-theme-alpine w-full h-full" :columnDefs="columnDefs"
        @grid-ready="onGridReady" :defaultColDef="defaultColDef" :rowData="exported_batches"></ag-grid-vue>
    </div>
    <table-paginator v-model="current_page" v-show="!filtered" :total_rows="total_exported_batches"
      :rows_per_page="rows_per_page" />
  </template>

  <loading-message v-else />
</template>

<script>
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridVue } from "ag-grid-vue3";
import GoToLink from "@/components/views/layout/ag_grid/GoToLink.vue";
import TablePaginator from "@/components/views/layout/TablePaginator.vue";
import Requester from "@/views/organization/requesters/process/harvest_export/show_process.js";
export default {
  name: "OrgProcessPackingShowProcess",
  components: { AgGridVue, TablePaginator },
  data() {
    return {
      load_data_general: false,
      current_page: 1,
      total_exported_batches: 0,
      rows_per_page: 12,
      rep_harvest_downloading: false,
      rep_packing_downloading: false,
      rep_consolidated_downloading: false,
      harvest_process: {},
      exported_batches: [],
      paginated_exported_batches: [],
      filtered: false,
      filtering: false,
      filters: {},
      defaultColDef: {
        sortable: true,
        filter: true,
        floatingFilter: false,
      },
      columnDefs: [
        {
          headerName: this.$t("model.harvested_batch.week"),
          field: "week",
          width: 300,
        },
        {
          headerName: this.$t("model.harvested_batch.registered_at"),
          field: "registered_at",
          cellRenderer: (params) => {
            return this.dateFormatter(params.value);
          },
          width: 300,
        },
        {
          headerName: this.$t("model.exported_batch.batch_code"),
          field: "batch_code",
          filter: true,
          cellRendererSelector: (params) => {
            return {
              component: GoToLink,
              params: {
                text: params.value,
                path_name: this.routeName("show_exported_batch"),
                params_path: {
                  organization_id: this.organization_id,
                  harvest_process_id: this.harvest_process_id,
                  exported_batch_id: params.data.id,
                },
              },
            };
          },
        },
        {
          headerName: this.$t("model.producer.producer_code"),
          field: "producer.producer_code",
          width: 300,
        },
        {
          headerName: this.$t(
            "model.producer.organization_producer.producer_code"
          ),
          field: "producer.producer_org_code",
          width: 300,
        },
        {
          headerName: this.$t("model.exported_batch.producer"),
          field: "producer.fullname",
          width: 300,
        },
        {
          headerName: this.$t("model.exported_batch.crop_field"),
          field: "crop_field.name",
          width: 300,
        },
        {
          headerName: this.$t("model.exported_batch.total_amount"),
          field: "total_amount",
          width: 300,
          filter: "agNumberColumnFilter",
          cellRenderer: (params) => {
            return this.numberWithPrecision(params.value);
          },
        },
        {
          headerName: this.$t("model.exported_batch.agro_exporter"),
          field: "agro_exporter_process.agro_exporter.name",
          width: 300,
        },
        {
          headerName: this.$t("model.exported_batch.packing_house"),
          field: "packing_house_process.packing_house.name",
          width: 300,
        },
      ],
    };
  },
  props: {
    organization_id: { required: true },
    harvest_process_id: { required: true },
  },
  computed: {
    can_filter() {
      return Object.values(this.filters).some(f => f !== "");
    }
  },
  methods: {
    loadData: async function () {
      await this.fetchHarvestProcess();
      await this.fetchTotalPages();
      await this.fetchExportedBatches();
      this.load_data_general = true;
    },
    fetchHarvestProcess: async function () {
      let response = await Requester.fetchHarvestProcess(
        this.organization_id,
        this.harvest_process_id
      );
      this.harvest_process = response.data;
    },
    fetchExportedBatches: async function () {
      let response = await Requester.fetchExportedBatches(
        this.organization_id,
        this.harvest_process_id,
        this.current_page - 1,
        this.rows_per_page,
      );
      this.exported_batches = response.data;
      this.paginated_exported_batches = response.data;
      this.exported_batches.forEach((harvested_batch) => {
        harvested_batch["harvested_packages_label"] =
          this.harvestedPackagesLabel(harvested_batch);
        harvested_batch["agro_exporter_process_name"] =
          this.agroExporterProcessName(harvested_batch);
        harvested_batch["harvest_team_label"] = this.harvestTeamLabel(
          harvested_batch.harvest_team
        );
      });
    },
    fetchTotalPages: async function () {
      let response = await Requester.fetchExportedBatchesTotal(
        this.organization_id,
        this.harvest_process_id
      );
      this.total_exported_batches = response.data;
      this.available_pages = Array.from(
        { length: Math.ceil(this.total_exported_batches / this.rows_per_page) },
        (_, i) => i + 1
      );
    },
    filterExportedBatches: async function () {
      if (this.filtereing) return;
      if (!this.can_filter) return;

      this.filtering = true;
      let response = await Requester.filterExportedBatches(
        this.organization_id,
        this.harvest_process_id,
        this.filters
      );

      this.exported_batches = response.data;
      this.filtered = true;
      this.filtering = false;
    },
    changePage: async function (new_page) {
      console.log(new_page);
      console.log(this.current_page);
      console.log("---");
      if (new_page != this.current_page) {
        console.log(new_page);

        //this.current_page = new_page;
        await this.fetchExportedBatches();
      }
    },
    clearFilters: async function () {
      this.filters = {};
      this.filtered = false;
      this.exported_batches = this.paginated_exported_batches;
    },
    downloadRepHarvestXlsx: async function () {
      this.rep_harvest_downloading = true;
      try {
        var response = await Requester.downloadRepHarvestXlsx(
          this.organization_id,
          this.harvest_process_id
        );
        const file = response.data;
        this.downloadFile(
          file,
          `Reporte de cosecha - ${this.harvest_process.name}.xlsx`
        );
      } catch (error) {
        error;
      }
      this.rep_harvest_downloading = false;
    },
    downloadRepPackingXlsx: async function () {
      this.rep_packing_downloading = true;
      try {
        var response = await Requester.downloadRepPackingXlsx(
          this.organization_id,
          this.harvest_process_id
        );
        const file = response.data;
        this.downloadFile(
          file,
          `Reporte de empaque - ${this.harvest_process.name}.xlsx`
        );
      } catch (error) {
        error;
      }
      this.rep_packing_downloading = false;
    },
    downloadRepConsolidatedXlsx: async function () {
      this.rep_consolidated_downloading = true;
      try {
        var response = await Requester.downloadRepConsolidatedXlsx(
          this.organization_id,
          this.harvest_process_id
        );
        const file = response.data;
        this.downloadFile(
          file,
          `Reporte consolidado cosecha-empaque - ${this.harvest_process.name}.xlsx`
        );
      } catch (error) {
        error;
      }
      this.rep_consolidated_downloading = false;
    },
    goToNewExportedBatch: function () {
      this.$router.push({
        name: this.routeName("new_exported_batch"),
        params: {
          organization_id: this.organization_id,
          harvest_process_id: this.harvest_process_id,
        },
      });
    },
    agroExporterProcessName: function (harvested_batch) {
      return [
        harvested_batch.agro_exporter_process?.name,
        harvested_batch.agro_exporter?.name,
      ];
    },
    harvestedPackagesLabel: function (harvested_batch) {
      return (harvested_batch.harvested_packages || [])
        .map(
          (harvested_package) =>
            `${harvested_package.number} ${harvested_package.crop_har_package.name}`
        )
        .join(", ");
    },
    harvestTeamLabel: function (harvest_team) {
      return [harvest_team?.harvest_team_code, harvest_team?.foreman];
    },
    sizeToFit() {
      this.gridApi.sizeColumnsToFit({
        defaultMinWidth: 150,
      });
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.sizeToFit();
    },
  },
  watch: {
    current_page: function () {
      this.fetchExportedBatches();
    }
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id,
        },
      }).then(() => this.loadData())
    );
  },
};
</script>
